import { RESET_TRANSITION } from './consts';
import { swap } from './utils';
import { layoutDropTargets } from './layoutDropTargets';
import { ElementRef } from './types';
import { DnDContext, getDnDContext } from './getDnDContext';
import { updateDragTarget } from './updateDragTarget';
import { getSwapDropTarget } from './getSwapDropTargets';

export const startDnD = (event: React.MouseEvent, containerRef: ElementRef) => {
  try {
    const controller = new AbortController();
    const dndContext = getDnDContext(event, containerRef, controller);
    if (!dndContext) return;
    event.preventDefault();

    controller.signal.addEventListener('abort', () => {
      /* empty */
    });

    document.addEventListener('mousemove', getMouseMoveHandler(dndContext), {
      signal: controller.signal,
    });
    document.addEventListener('mouseup', getMouseUpHandler(dndContext), {
      signal: controller.signal,
    });
  } catch (e) {
    /* empty */
  }
};

// The event handlers are registered with a signal of an AbortController
// which lets the handlers be removed by calling abort on the according
// controller.

const getMouseMoveHandler =
  (dndContext: NonNullable<DnDContext>) => (event: MouseEvent) => {
    try {
      const { eventPosition, scrollValues } = dndContext;
      const { xStart, yStart, relativeX, relativeY } = eventPosition;
      const { clientX, clientY } = event;
      const { dragTarget, ghostContainer, dropTargets, dragTargetPlaceholder } =
        dndContext;
      const translateX = clientX - xStart;
      const translateY = clientY - yStart;
      eventPosition.currentX = relativeX + translateX + scrollValues.deltaLeft;
      eventPosition.currentY = relativeY + translateY + scrollValues.deltaTop;
      updateDragTarget(
        dragTarget,
        ghostContainer,
        translateX,
        translateY,
        scrollValues
      );
      const swapTarget = getSwapDropTarget(dndContext);
      if (!swapTarget || dndContext.lastSwapTarget === swapTarget) return;
      const { currentX, currentY } = swapTarget;
      const isSwapped = swap(
        dragTargetPlaceholder.currentIndex,
        swapTarget.currentIndex,
        dropTargets
      );
      layoutDropTargets(dndContext);
      // Special case if the first item in a row does not move even though the
      // items have been swapped. That can happen if the first item in the row
      // is quite wide and the drag target quite small, e.g. the drag target
      // fits in the row above, but not the first item.
      const isSameFirstPosition =
        isSwapped &&
        currentX === 0 &&
        currentX === swapTarget.currentX &&
        currentY === swapTarget.currentY;
      dndContext.lastSwapTarget = isSameFirstPosition ? swapTarget : null;
    } catch (e) {
      dndContext.controller.abort();
    }
  };

const getMouseUpHandler = (dndContext: NonNullable<DnDContext>) => () => {
  try {
    const {
      dragTarget,
      dropTargets,
      containerProps: { refBoundOnSortEnd },
      ghostContainer,
      scrollValues,
      dragTargetPlaceholder: { currentIndex, translateX, translateY },
    } = dndContext;
    const { setStateWithRefCheck, initialIndex } = dragTarget;
    const resetDragTarget = () => {
      try {
        setStateWithRefCheck({});
      } catch (e) {
        /* empty */
      }
    };
    updateDragTarget(
      dragTarget,
      ghostContainer,
      translateX - scrollValues.deltaLeft,
      translateY - scrollValues.deltaTop,
      scrollValues,
      { transition: `transform ${RESET_TRANSITION}ms` },
      resetDragTarget
    );
    // To be sure that the drag target gets reset.
    setTimeout(resetDragTarget, RESET_TRANSITION);
    refBoundOnSortEnd({
      oldIndex: initialIndex,
      newIndex: currentIndex,
    });
    dropTargets.forEach(
      ({ setStateWithRefCheck, isDragTarget }) =>
        !isDragTarget && setStateWithRefCheck({})
    );
  } finally {
    dndContext.controller.abort();
  }
};
